import { useState, useEffect } from "react";
import axios from "axios";
import CONSTANTS from "../utils/constants";
import { useNavigate, useParams } from "react-router-dom";
import PrintPage from "./PrintPage";
import { useTranslation } from "react-i18next"; // ✅ i18n import

const paymentMapping = {
  nagt: "nagt",
  наличными: "nagt",
  cash: "nagt",
  price: "nagt",
  kart: "kart",
  карточкой: "kart",
  карта: "kart",
  card: "kart",
  terminal: "kart",
  online: "online",
  онлайн: "online",
};

const statusMapping = {
  Garaşylýar: "garasylyar",
  "В ожидании": "garasylyar",
  "Kabul edilen": "kabul_edilen",
  Принято: "kabul_edilen",
  "Iberildi ýolda": "iberildi_yolda",
  "В пути": "iberildi_yolda",
  Tamamlandy: "tamamlandy",
  Завершено: "tamamlandy",
  "Ret edilen": "ret_edilen",
  Отклонено: "ret_edilen",
};

export default function AdminOrderDetails() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPrint, setShowPrint] = useState(false);
  const [previousOrders, setPreviousOrders] = useState([]);
  const [showOrderHistory, setShowOrderHistory] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(`${CONSTANTS.baseRoute}/orders/${id}`);
        setOrder(response.data);
      } catch (err) {
        console.error("Error:", err);
        setError(t("order_load_error"));
        setOrder(null);
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchOrderDetails();
  }, [id, t]);

  useEffect(() => {
    const fetchPreviousOrders = async () => {
      if (!order?.user_uuid) {
        setPreviousOrders([]);
        return;
      }
      try {
        const response = await axios.get(
          `${CONSTANTS.baseRoute}/orders/user/${order.user_uuid}`,
          { params: { excludeId: order.id } },
        );
        setPreviousOrders(response.data);
      } catch (err) {
        console.error("Error:", err);
        setPreviousOrders([]);
      }
    };

    fetchPreviousOrders();
  }, [order?.user_uuid, order?.id]);

  const handlePrint = () => {
    if (!order) return;
    setShowPrint(true);
    setTimeout(() => {
      window.print();
      setShowPrint(false);
    }, 100);
  };

  if (loading) return <div className="p-4">{t("yuklenyar")}...</div>;
  if (error) return <div className="text-danger p-4">{error}</div>;
  if (!order) return <div className="p-4">{t("sargyt_tapylmady")}</div>;

  const orderDetails = order;
  const orderItems = order.items;

  // Statü Butonları (Çevirili)
  const statuses = [
    { key: "Garaşylýar", label: t("garasylyar") },
    { key: "Kabul edilen", label: t("kabul_edilen") },
    { key: "Iberildi ýolda", label: t("iberildi_yolda") },
    { key: "Tamamlandy", label: t("tamamlandy") },
    { key: "Ret edilen", label: t("ret_edilen") },
  ];

  // order_items.quantity is numeric(10,3) now (fractional for kg/gr
  // products), so trim the trailing ".000" for whole-unit quantities.
  const formatQuantity = (value) => {
    const n = parseFloat(value);
    if (isNaN(n)) return value;
    return n % 1 === 0 ? n.toString() : n.toFixed(3).replace(/0+$/, "").replace(/\.$/, "");
  };

  const orderGrandTotal = (o) =>
    Number(o?.total_price || 0) + Number(o?.delivery_price || 0);

  const orderDebt = (o) => {
    if (o?.paid_amount === null || o?.paid_amount === undefined) return null;
    return Math.max(orderGrandTotal(o) - Number(o.paid_amount || 0), 0);
  };

  const handleStatusChange = async (newStatus) => {
    let paidAmount;

    if (newStatus === "Tamamlandy" && orderDetails.user_uuid) {
      const grandTotal = orderGrandTotal(orderDetails);
      const input = window.prompt(
        t("berlen_pul_sorag"),
        grandTotal.toFixed(2),
      );
      if (input === null) return;
      const parsed = parseFloat(input.replace(",", "."));
      if (isNaN(parsed) || parsed < 0) {
        window.alert(t("berlen_pul_nadogry"));
        return;
      }
      paidAmount = parsed;
    }

    try {
      await axios.put(`${CONSTANTS.baseRoute}/orders/${id}/status`, {
        status: newStatus,
        ...(paidAmount !== undefined && { paid_amount: paidAmount }),
      });
      setOrder({
        ...order,
        status: newStatus,
        ...(paidAmount !== undefined && { paid_amount: paidAmount }),
      });
    } catch (err) {
      console.error(err);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      Garaşylýar: "text-warning",
      "Kabul edilen": "text-info",
      "Iberildi ýolda": "text-primary",
      Tamamlandy: "text-success",
      "Ret edilen": "text-danger",
    };
    return colors[status] || "";
  };

  const getPaymentLabel = (method) => {
    const key = paymentMapping[method?.toLowerCase()] || method;
    return t(key);
  };

  return (
    <div className="admin-main py-2 px-3">
      <div className="d-flex gap-2 align-items-center mb-2">
        <button className="btn admin-icon-btn" onClick={() => navigate(-1)}>
          <i className="bi bi-chevron-left"></i>
        </button>
        <button className="btn admin-icon-btn" onClick={() => navigate(+1)}>
          <i className="bi bi-chevron-right"></i>
        </button>
        <h5 className="fw-bold">
          {t("sargyt")} № <span>{orderDetails.id}</span>
        </h5>
      </div>

      <div className="non-printable">
        <div className="d-flex flex-wrap justify-content-between mb-2 gap-2">
          <div className="d-flex flex-wrap gap-2">
            {statuses.map((st) => {
              const isActive = orderDetails.status === st.key;
              return (
                <button
                  key={st.key}
                  className={`rounded-1 px-3 py-1 small ${
                    isActive ? "admin-hover-active" : "admin-hover"
                  }`}
                  onClick={() => !isActive && handleStatusChange(st.key)}
                  disabled={isActive}
                >
                  {st.label}
                </button>
              );
            })}
          </div>
          <button
            onClick={handlePrint}
            className="btn d-flex gap-2 align-items-center bg-light border small"
          >
            <i className="bi bi-printer"></i>
            <span>{t("cap_etmek")}</span>
          </button>
        </div>

        <div className="row g-3 d-flex">
          {/* Ürün Tablosu */}
          <div className="table-responsive card p-2 shadow-sm col-md-8">
            <table className="table align-middle table-hover text-center small">
              <thead>
                <tr>
                  <th style={{ color: "#a9a9a9" }}>№</th>
                  <th style={{ color: "#a9a9a9" }}>{t("surat")}</th>
                  <th
                    className="text-start"
                    style={{ width: "25%", color: "#a9a9a9" }}
                  >
                    {t("ady")}
                  </th>
                  <th style={{ color: "#a9a9a9" }}>{t("barkod")}</th>
                  <th style={{ color: "#a9a9a9" }}>{t("sany")}</th>
                  <th style={{ color: "#a9a9a9" }}>{t("bahasy")}</th>
                  <th style={{ color: "#a9a9a9" }}>{t("arzanladys")}</th>
                  <th style={{ color: "#a9a9a9" }}>{t("jemi")}</th>
                </tr>
              </thead>
              <tbody>
                {orderItems.map((item, index) => {
                  const originalPrice = Number(item.price || 0);

                  // 🔥 0 veya 0% veya null ise indirim yok say
                  const discountValue = Number(item.discount);
                  const hasDiscount =
                    item.discount !== null &&
                    item.discount !== undefined &&
                    discountValue > 0;

                  const discountedPrice = hasDiscount
                    ? discountValue
                    : originalPrice;

                  const lineTotal = (item.quantity * discountedPrice).toFixed(
                    2,
                  );
                  return (
                    <tr key={index}>
                      <td className="fw-bold text-danger">{index + 1}</td>
                      <td>
                        <img
                          src={
                            item.image
                              ? `${CONSTANTS.baseRoute}${item.image}`
                              : "/logo.png"
                          }
                          alt=""
                          width={60}
                          height={60}
                          className={`object-fit-contain border rounded ${
                            !item.image ? "p-2" : ""
                          }`}
                        />
                      </td>

                      <td className="text-start">
                        {i18n.language === "ru"
                          ? item.product_name_ru
                          : item.product_name_tm}
                      </td>
                      <td>{item.product_barcode}</td>
                      <td>x {formatQuantity(item.quantity)}</td>
                      <td>{originalPrice.toFixed(2)} m</td>
                      <td>
                        {hasDiscount ? `${discountedPrice.toFixed(2)} m` : "-"}
                      </td>
                      <td className="fw-bold">{lineTotal} m</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Sipariş Detay Kartı */}
          <div className="col-md-4">
            <div className="p-3 shadow-sm card" style={{ fontSize: 16 }}>
              {[
                { label: t("ulanyjynyn_ady"), value: orderDetails.full_name },
                {
                  label: t("doredilen_sene"),
                  value: new Date(orderDetails.created_at).toLocaleString(),
                },
                { label: t("eltmeli_wagty"), value: orderDetails.time },
                { label: t("sargyt_nomeri"), value: orderDetails.id },
                {
                  label: t("yagdayy"),
                  value: t(
                    statusMapping[orderDetails.status] || orderDetails.status,
                  ),
                  className: getStatusColor(orderDetails.status),
                },
                {
                  label: t("telefon_belgisi"),
                  value: orderDetails.phone_number,
                },
                {
                  label: t("salgysy"),
                  value: `${orderDetails.region}, ${orderDetails.city || ""}, ${orderDetails.address_details || ""}`,
                },
                { label: t("bellik"), value: orderDetails.notes || t("yok") },
                {
                  label: t("toleg_gornusi"),
                  value: getPaymentLabel(orderDetails.payment_method),
                },
              ].map((item, idx) => (
                <div key={idx}>
                  <div className="d-flex justify-content-between py-2 fw-semibold">
                    <span className="text-muted">{item.label}:</span>
                    <span className={item.className || ""}>
                      {item.value || "N/A"}
                    </span>
                  </div>
                  <hr className="my-0 opacity-25" />
                </div>
              ))}

              {orderDetails.paid_amount !== null &&
                orderDetails.paid_amount !== undefined && (
                  <div>
                    <div className="d-flex justify-content-between py-2 fw-semibold">
                      <span className="text-muted">{t("berlen_pul")}:</span>
                      <span>
                        {Number(orderDetails.paid_amount).toFixed(2)} m
                      </span>
                    </div>
                    <hr className="my-0 opacity-25" />
                    {orderDebt(orderDetails) > 0 && (
                      <>
                        <div className="d-flex justify-content-between py-2 fw-semibold">
                          <span className="text-muted">{t("borc")}:</span>
                          <span className="text-danger fw-bold">
                            {orderDebt(orderDetails).toFixed(2)} m
                          </span>
                        </div>
                        <hr className="my-0 opacity-25" />
                      </>
                    )}
                  </div>
                )}

              {orderDetails.user_uuid && (
                <div>
                  {previousOrders.reduce(
                    (sum, po) => sum + (orderDebt(po) || 0),
                    0,
                  ) > 0 && (
                    <div className="d-flex justify-content-between py-2 fw-semibold">
                      <span className="text-muted">
                        {t("mushderinin_umumy_borjy")}:
                      </span>
                      <span className="text-danger fw-bold">
                        {previousOrders
                          .reduce((sum, po) => sum + (orderDebt(po) || 0), 0)
                          .toFixed(2)}{" "}
                        m
                      </span>
                    </div>
                  )}
                  <div className="d-flex justify-content-between align-items-center py-2 fw-semibold">
                    <span className="text-muted">{t("onki_sargytlar")}:</span>
                    <div className="d-flex align-items-center gap-2">
                      {previousOrders.length === 0 ? (
                        <span className="badge bg-success-subtle text-success">
                          {t("onki_sargyt_yok")}
                        </span>
                      ) : (
                        <span className="badge bg-primary-subtle text-primary">
                          {previousOrders.length + 1}
                        </span>
                      )}
                      {previousOrders.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setShowOrderHistory((prev) => !prev)}
                          className="btn btn-sm btn-light border p-0 d-flex align-items-center justify-content-center"
                          style={{ width: 24, height: 24 }}
                        >
                          <i
                            className="bi bi-chevron-down"
                            style={{
                              transition: "transform 0.15s",
                              transform: showOrderHistory
                                ? "rotate(180deg)"
                                : "none",
                            }}
                          ></i>
                        </button>
                      )}
                    </div>
                  </div>

                  {showOrderHistory && previousOrders.length > 0 && (
                    <div className="d-flex flex-column gap-1 py-1">
                      {previousOrders.map((po) => (
                        <div
                          key={po.id}
                          className="d-flex justify-content-between align-items-center"
                        >
                          <span
                            role="button"
                            onClick={() =>
                              navigate(`/admin/home/orders/${po.id}`)
                            }
                            className="text-primary text-decoration-underline"
                          >
                            {t("sargyt")} №{po.id} (
                            {new Date(po.created_at).toLocaleDateString()})
                          </span>
                          <span className="d-flex align-items-center gap-2">
                            {orderDebt(po) > 0 && (
                              <span className="text-danger fw-bold">
                                {t("borc")}: {orderDebt(po).toFixed(2)} m
                              </span>
                            )}
                            <span className={`fw-bold ${getStatusColor(po.status)}`}>
                              {t(statusMapping[po.status] || po.status)}
                            </span>
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  <hr className="my-0 opacity-25" />
                </div>
              )}

              <div className="mt-3 p-2 bg-light rounded">
                <div className="d-flex justify-content-between py-1 fw-bold">
                  <span>{t("eltip_bermek")}:</span>
                  <span>
                    {Number(orderDetails.delivery_price || 0).toFixed(2)} m
                  </span>
                </div>
                <div className="d-flex justify-content-between py-1 fw-bold">
                  <span>{t("subtotal")}:</span>
                  <span>
                    {Number(orderDetails.total_price || 0).toFixed(2)} m
                  </span>
                </div>
                <hr />
                <div className="d-flex justify-content-between fs-5 fw-bold text-primary">
                  <span>{t("jemi_bahasy")}:</span>
                  <span>
                    {(
                      Number(orderDetails.total_price || 0) +
                      Number(orderDetails.delivery_price || 0)
                    ).toFixed(2)}{" "}
                    m
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showPrint && <PrintPage order={order} />}
    </div>
  );
}
